import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  Text,
  Input,
  Box,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Progress,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { useState, useRef } from 'react';
import { parseUsersCSV, type CSVParseResult } from '@/utility/csv';

interface ImportUsersDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (result: CSVParseResult) => Promise<void>;
  isLoading?: boolean;
}

interface ImportResult {
  success: number;
  errors: Array<{
    row: number;
    name: string;
    error: string;
  }>;
  created: number;
  updated: number;
}

export const ImportUsersDialog = ({
  isOpen,
  onClose,
  onImport,
  isLoading = false,
}: ImportUsersDialogProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<CSVParseResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [step, setStep] = useState<'upload' | 'preview' | 'results'>('upload');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: 'Invalid file type',
        description: 'Please select a CSV file',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    if (file.size > 1024 * 1024) { // 1MB limit
      toast({
        title: 'File too large',
        description: 'Please select a file smaller than 1MB',
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setSelectedFile(file);
    
    try {
      const result = await parseUsersCSV(file);
      setParseResult(result);
      setStep('preview');
    } catch (error: any) {
      toast({
        title: 'Failed to parse CSV',
        description: error.message || 'Invalid CSV format',
        status: 'error',
        duration: 3000,
      });
      setSelectedFile(null);
    }
  };

  const handleImport = async () => {
    if (!parseResult) return;

    try {
      await onImport(parseResult);
      setStep('results');
      
      // Mock result for now - will be replaced with actual API response
      setImportResult({
        success: parseResult.users.length - parseResult.errors.length,
        errors: parseResult.errors.map(err => ({
          row: err.row,
          name: parseResult.users.find(u => u.rowNumber === err.row)?.name || 'Unknown',
          error: err.error
        })),
        created: parseResult.newUsers.length,
        updated: parseResult.existingUsers.length,
      });
    } catch (error: any) {
      toast({
        title: 'Import failed',
        description: error.message || 'Something went wrong',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setParseResult(null);
    setImportResult(null);
    setStep('upload');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  const renderUploadStep = () => (
    <VStack spacing={4}>
      <Text fontSize="sm" color="gray.600">
        Upload a CSV file with columns: id, name, country_code, phone_number
      </Text>
      <Text fontSize="xs" color="gray.500">
        Leave ID empty for new users, include ID for updates
      </Text>
      
      <Input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileSelect}
        display="none"
        data-testid="import-users-file-input"
      />

      <Button
        onClick={() => fileInputRef.current?.click()}
        variant="outline"
        size="lg"
        width="full"
        data-testid="import-users-choose-file-btn"
      >
        Choose CSV File
      </Button>
      
      {selectedFile && (
        <Text fontSize="sm" color="green.600">
          Selected: {selectedFile.name}
        </Text>
      )}
    </VStack>
  );

  const renderPreviewStep = () => {
    if (!parseResult) return null;

    const hasErrors = parseResult.errors.length > 0;
    const validUsers = parseResult.users.length - parseResult.errors.length;

    return (
      <VStack spacing={4} align="stretch">
        <Alert status={hasErrors ? 'warning' : 'success'}>
          <AlertIcon />
          <Box>
            <AlertTitle>
              {hasErrors ? 'Issues Found' : 'Ready to Import'}
            </AlertTitle>
            <AlertDescription>
              {validUsers} valid users found ({parseResult.newUsers.length} new, {parseResult.existingUsers.length} updates)
              {hasErrors && `, ${parseResult.errors.length} errors to fix`}
            </AlertDescription>
          </Box>
        </Alert>

        {hasErrors && (
          <Accordion allowToggle>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="medium">
                    View Errors ({parseResult.errors.length})
                  </Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Row</Th>
                      <Th>Field</Th>
                      <Th>Value</Th>
                      <Th>Error</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parseResult.errors.map((error, index) => (
                      <Tr key={index}>
                        <Td>{error.row}</Td>
                        <Td>{error.field}</Td>
                        <Td>{error.value || '-'}</Td>
                        <Td color="red.500">{error.error}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}

        {validUsers > 0 && (
          <Accordion allowToggle>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="medium">
                    Preview Valid Users ({validUsers})
                  </Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Action</Th>
                      <Th>Name</Th>
                      <Th>Country Code</Th>
                      <Th>Phone Number</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {parseResult.users
                      .filter(user => !parseResult.errors.some(err => err.row === user.rowNumber))
                      .slice(0, 10) // Show first 10 for preview
                      .map((user, index) => (
                      <Tr key={index}>
                        <Td>
                          <Badge colorScheme={user.id ? 'blue' : 'green'}>
                            {user.id ? 'Update' : 'Create'}
                          </Badge>
                        </Td>
                        <Td>{user.name}</Td>
                        <Td>{user.country_code}</Td>
                        <Td>{user.phone_number}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
                {validUsers > 10 && (
                  <Text fontSize="xs" color="gray.500" mt={2}>
                    Showing first 10 of {validUsers} users
                  </Text>
                )}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
      </VStack>
    );
  };

  const renderResultsStep = () => {
    if (!importResult) return null;

    const hasErrors = importResult.errors.length > 0;

    return (
      <VStack spacing={4} align="stretch">
        <Alert status={hasErrors ? 'warning' : 'success'}>
          <AlertIcon />
          <Box>
            <AlertTitle>Import Complete</AlertTitle>
            <AlertDescription>
              {importResult.success} users processed successfully
              ({importResult.created} created, {importResult.updated} updated)
              {hasErrors && `, ${importResult.errors.length} failed`}
            </AlertDescription>
          </Box>
        </Alert>

        {hasErrors && (
          <Accordion allowToggle>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  <Text fontWeight="medium">
                    View Failures ({importResult.errors.length})
                  </Text>
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel pb={4}>
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>Row</Th>
                      <Th>Name</Th>
                      <Th>Error</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {importResult.errors.map((error, index) => (
                      <Tr key={index}>
                        <Td>{error.row}</Td>
                        <Td>{error.name}</Td>
                        <Td color="red.500">{error.error}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        )}
      </VStack>
    );
  };

  return (
    <Modal 
      closeOnOverlayClick={false} 
      isOpen={isOpen} 
      onClose={handleClose} 
      size="xl"
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent bg="bg-surface" maxH="80vh" overflow="hidden">
        <ModalHeader color="gray.700">
          Import Users
          {step === 'preview' && parseResult && (
            <Text fontSize="sm" fontWeight="normal" color="gray.600">
              {parseResult.users.length} users found in {selectedFile?.name}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton color="gray.700" />
        
        <ModalBody overflowY="auto">
          {isLoading && (
            <Box mb={4}>
              <Progress isIndeterminate size="sm" colorScheme="brand" />
              <Text fontSize="sm" color="gray.600" mt={2}>
                Processing import...
              </Text>
            </Box>
          )}
          
          {step === 'upload' && renderUploadStep()}
          {step === 'preview' && renderPreviewStep()}
          {step === 'results' && renderResultsStep()}
        </ModalBody>

        <ModalFooter gap={3}>
          {step === 'upload' && (
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
          )}
          
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('upload')}>
                Back
              </Button>
              <Button
                onClick={handleImport}
                isLoading={isLoading}
                loadingText="Importing"
                isDisabled={!parseResult || parseResult.errors.length === parseResult.users.length}
                data-testid="import-users-confirm-btn"
              >
                Import {parseResult && parseResult.users.length - parseResult.errors.length} Users
              </Button>
            </>
          )}

          {step === 'results' && (
            <Button onClick={handleClose} data-testid="import-users-done-btn">
              Done
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportUsersDialog;